<template>
  <section
    ref="elRef"
    class="PastRunTimelineView"
    data-testid="pastRunTimeline"
    :aria-busy="isFetching"
  >
    <Transition>
      <EmptyState
        v-if="hasFetched && pastRuns.length === 0"
        class="PastRunTimelineView__emptyState"
        data-testid="pastRunTimelineEmptyState"
        iconName="calendarX"
        :title="currentRuns.length > 0 ? 'No more past runs' : 'No past runs'"
        :subtitle="`Time to let go of the past\nand focus on your next shot`"
      />
      <RunTimeline
        v-else
        class="PastRunTimelineView__timeline"
        sort="desc"
        :runs="pastRuns"
        isInPast
      />
    </Transition>
  </section>
</template>

<script lang="ts" setup>
import EmptyState from "@/components/EmptyState.vue";
import RunTimeline from "@/components/RunTimeline.vue";
import useRunCarouselStore from "@/stores/useRunCarouselStore";
import { storeToRefs } from "pinia";
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";

const {
  pastRuns,
  currentRuns,
} = storeToRefs(useRunCarouselStore());

const {
  fetchPastRuns,
} = useRunCarouselStore();

const elRef = useTemplateRef<HTMLDivElement>("elRef");
const isFetching = ref(false);
const hasFetched = ref(false);

const intersectionObserver = new IntersectionObserver(handleIntersection, {
  threshold: 0.5,
});

onMounted(() => {
  if (elRef.value) intersectionObserver.observe(elRef.value);
});

onBeforeUnmount(() => {
  intersectionObserver.disconnect();
});

async function handleIntersection (entries: Array<IntersectionObserverEntry>) {
  if (isFetching.value) return;

  const entry = entries.find((entry) => entry.isIntersecting);
  if (!entry) return;

  isFetching.value = true;
  const [error] = await fetchPastRuns();
  isFetching.value = false;

  if (!error) hasFetched.value = true;
}
</script>

<style lang="scss" scoped>
.PastRunTimelineView {
  position: relative;
  height: 100%;
  min-height: 400px;
}

.PastRunTimelineView__timeline,
.PastRunTimelineView__emptyState {
  transition: 350ms opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.PastRunTimelineView__emptyState {
  position: absolute;
  inset: 0;

  border: solid 2px var(--themeDarkTransparent100);
  border-radius: 6px;
}
</style>
